/**
 * Dealer Desk actions on one account (phase 3): Open position (pop-up), cancel a pending
 * order, close all, and money actions. Market orders fill at the live price.
 */
import { createContext, useContext, useState, type ReactNode } from 'react';
import { fmtUsd } from '@platform/shared';
import { cx, Icon, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { createStore, useStore } from '../lib/store';
import { useLoad } from '../stores/account';
import { OpenPositionDialog } from './AdminDeskPositionDialogs';

const input = 'h-9 rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const small = 'h-7 rounded-md border border-line-strong px-2 text-[11px] font-semibold text-fg disabled:opacity-40';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

interface Perm {
  permission: string;
  act: 'none' | 'request' | 'direct';
}
/** The account figures the pop-ups show in their header strip. */
export interface DeskSummary {
  label: string;
  balance: string;
  credit: string;
  equity: string;
  openPnl: string;
  freeMargin: string;
  marginLevel: string | null;
  /** For the trade estimates: margin in use and the stop-out margin level (e.g. "0.5"). */
  marginUsed?: string;
  stopOut?: string;
  /** Open positions in lots, e.g. "2.70". */
  openLots?: string;
}
/**
 * What the Open position pop-up checks before the click besides the instrument and the price
 * (DEALER_OPEN_REFUSALS_PLAN.md): the client's restrictions and leverage cap, or a master's caps.
 */
export interface DeskLimits {
  client: { tradingStatus: string; reason: string | null; maxLeverage: number | null; crmId: string | null } | null;
  master: { id: string; name: string; maxOrder: string | null; maxLeverage: number | null; instruments: string[] } | null;
}
export interface DeskCtx {
  accountId: string;
  summary: DeskSummary | null;
  limits: DeskLimits | null;
  canTrade: boolean;
  /** May request (or post) price / cancel / commission corrections, and rollover adjustments; on a master account too. */
  canCorrect: boolean;
  canRollover: boolean;
  /** May ask for a transferred trade's previous-broker figures (always approved by someone else). */
  canTransfer: boolean;
  /** May edit a trade's type, amount, leverage, prices and times (directly, or as a request); on a master account too. */
  canEdit: boolean;
  /**
   * May open or close a trade at a past price and minute picked on the chart (trade.dealer and trade.edit,
   * both direct). On a master too: its followers' copies open and close at the same price and minute.
   */
  canBackdate: boolean;
  /** A master account (Admin → Masters): its followers are the clients shown online on the chart. */
  master: boolean;
  /** The master's id, for its followers' copies of a trade. */
  masterId: string | null;
  /** The highest leverage a trade here may take below the instrument's own: the client's max leverage, or the master's cap. */
  leverageCap: number | null;
  /** May restore a closed trade (directly, or as a request); restoreDirect: without approval. On a master account too. */
  canRestore: boolean;
  restoreDirect: boolean;
  /** Refresh the account page after an action. */
  refresh: () => void;
}
const Ctx = createContext<DeskCtx | null>(null);
export const useDesk = () => useContext(Ctx);

/**
 * What a trade pop-up did, in the corner once it has closed (DEALER_POPUP_CLOSE_PLAN.md): the pop-ups
 * close as soon as their action goes through, so the result is said here. `fail`: part of it did not
 * go through; that note stays until it is closed, the rest go by themselves.
 */
interface DeskNote {
  id: number;
  title: string;
  lines: string[];
  fail?: string | null;
}
const notes = createStore<{ list: DeskNote[] }>({ list: [] });
const NOTE_MS = 7000;
let noteSeq = 0;
const dismissNote = (id: number) => notes.set((s) => ({ list: s.list.filter((n) => n.id !== id) }));
export function deskNotice(n: Omit<DeskNote, 'id'>) {
  const id = ++noteSeq;
  notes.set((s) => ({ list: [{ ...n, id }, ...s.list].slice(0, 3) }));
  if (!n.fail) window.setTimeout(() => dismissNote(id), NOTE_MS);
}

/** The notes' corner, once on the admin page. */
export function DeskNotices() {
  const list = useStore(notes, (s) => s.list);
  if (!list.length) return null;
  return (
    <div aria-live="polite" className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-[min(380px,calc(100vw-32px))] flex-col gap-2">
      {list.map((n) => (
        <div key={n.id} role="status" className={cx('shadow-pop sheet-in pointer-events-auto flex items-start gap-2.5 rounded-xl border bg-panel py-3 pr-2 pl-3.5', n.fail ? 'border-down/50' : 'border-line')}>
          <span aria-hidden="true" className={cx('pt-px text-sm font-semibold', n.fail ? 'text-down' : 'text-up')}>
            {n.fail ? '!' : '✓'}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-[13px] font-semibold">{n.title}</span>
            {n.lines.map((l, i) => (
              <span key={i} className="text-[12.5px] leading-snug text-muted">
                {l}
              </span>
            ))}
            {n.fail && <span className="text-[12.5px] leading-snug text-down">{n.fail}</span>}
          </div>
          <button type="button" aria-label="Dismiss" onClick={() => dismissNote(n.id)} className="grid size-7 shrink-0 place-items-center rounded-lg text-muted hover:text-fg">
            <Icon name="close" size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}

/** Provides the permissions to the actions on one account page. */
export function DeskActions({
  accountId,
  summary = null,
  limits = null,
  refresh,
  master = false,
  masterId = null,
  leverageCap = null,
  children,
}: {
  accountId: string;
  summary?: DeskSummary | null;
  limits?: DeskLimits | null;
  refresh: () => void;
  /** A master account (Admin → Masters): trading, and the same pop-ups as a client's trade; no money actions or transfers. */
  master?: boolean;
  masterId?: string | null;
  leverageCap?: number | null;
  children: (bar: ReactNode, ctx: DeskCtx | null) => ReactNode;
}) {
  const me = useLoad<{ permissions: Perm[] }>('/api/admin/desk/me');
  // The role's own right (Admin → Settings → Staff & roles): an admin and a dealer get what their role allows,
  // on a client's account and a master's alike. `can` is a client's account only (money, transfers).
  const role = (p: string, act: Perm['act'][]) => !!me.data?.permissions.some((x) => x.permission === p && act.includes(x.act));
  const can = (p: string, act: Perm['act'][]) => !master && role(p, act);
  const canTrade = master ? !!me.data : can('trade.dealer', ['direct']);
  const canMoney = ['money.deposit', 'money.withdraw', 'money.credit_in', 'money.credit_out', 'money.zero_balance', 'money.balance_transfer'].some((p) => can(p, ['direct', 'request']));
  // A master's trade takes the same edits, corrections and restore as a client's (MASTER_EDITS_PLAN.md); they change
  // the master's trade only.
  const canCorrect = role('position.correction', ['direct', 'request']);
  const canRollover = role('money.rollover', ['direct', 'request']);
  const canTransfer = can('trade.transfer', ['direct', 'request']);
  const canEdit = role('trade.edit', ['direct', 'request']);
  // From chart on a master too (Leon, 2026-10-20): its followers' copies open at the same price and minute.
  const canBackdate = canTrade && role('trade.edit', ['direct']);
  const canRestore = role('trade.restore', ['direct', 'request']);
  const restoreDirect = role('trade.restore', ['direct']);
  const ctx: DeskCtx = { accountId, summary, limits, canTrade, canCorrect, canRollover, canTransfer, canEdit, canBackdate, master, masterId, leverageCap, canRestore, restoreDirect, refresh };
  const bar = me.data && (canTrade || canMoney) && (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {canTrade && <OpenPositionButton ctx={ctx} />}
        {canMoney && <MoneyAction ctx={ctx} />}
        {canTrade && <CloseAll ctx={ctx} />}
      </div>
    </div>
  );
  return <Ctx.Provider value={ctx}>{children(bar || null, ctx)}</Ctx.Provider>;
}

/** A small disclosure: a button that opens its panel below. */
function Panel({ id, label, tone, children }: { id?: string; label: string; tone?: string; children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={cx('flex flex-col gap-2', open && 'w-full')}>
      <button id={id} type="button" className={cx('h-9 self-start rounded-lg border px-3 text-xs font-semibold', tone ?? 'border-line-strong text-fg')} aria-expanded={open} onClick={() => setOpen(!open)}>
        {label}
      </button>
      {open && <div className="rounded-xl border border-line bg-panel p-3">{children(() => setOpen(false))}</div>}
    </div>
  );
}

/** The Money action button's id: a refusal's "Add funds" (OpenPositionDialog) opens it. */
export const MONEY_BUTTON = 'desk-money-action';

/** "Open position…": the pop-up (OpenPositionDialog) with the live Sell / Buy prices. */
function OpenPositionButton({ ctx }: { ctx: DeskCtx }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="h-9 rounded-lg border border-accent px-3 text-xs font-semibold text-accent" onClick={() => setOpen(true)}>
        Open position…
      </button>
      {open && <OpenPositionDialog ctx={ctx} onClose={() => setOpen(false)} />}
    </>
  );
}

const MONEY: { id: string; label: string; amount: boolean }[] = [
  { id: 'deposit', label: 'Deposit', amount: true },
  { id: 'withdraw', label: 'Withdraw', amount: true },
  { id: 'credit_in', label: 'Credit in', amount: true },
  { id: 'credit_out', label: 'Credit out', amount: true },
  { id: 'zero_balance', label: 'Zero balance', amount: false },
  { id: 'balance_transfer', label: 'Balance from previous broker', amount: true },
];

function MoneyAction({ ctx }: { ctx: DeskCtx }) {
  const [action, setAction] = useState('deposit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const needsAmount = MONEY.find((m) => m.id === action)!.amount;
  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<Record<string, any>>(`/api/admin/desk/accounts/${ctx.accountId}/money`, { action, amount: needsAmount ? amount : undefined, reason });
      setMsg({
        ok: true,
        text: r.pendingApproval
          ? `Sent for approval as APR-${r.pendingApproval.number}. It runs once someone else approves it.`
          : r.balanceAfter !== undefined
            ? `Done. Balance ${fmtUsd(r.balanceAfter)}.`
            : `Done. Credit ${fmtUsd(r.creditAfter)}.`,
      });
      setAmount('');
      setReason('');
      ctx.refresh();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel id={MONEY_BUTTON} label="Money action…">
      {() => (
        <div className="flex flex-col gap-2">
          <div role="radiogroup" aria-label="Money action" className="flex flex-wrap gap-1.5">
            {MONEY.map((m) => (
              <button key={m.id} type="button" role="radio" aria-checked={action === m.id} className={cx(small, 'h-8 px-3', action === m.id && 'border-accent bg-accent/10 text-accent')} onClick={() => setAction(m.id)}>
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {needsAmount && (
              <span className="relative flex items-center">
                <span className="pointer-events-none absolute left-3 text-[13px] text-muted">$</span>
                <input className={cx(input, 'num w-36 pl-7')} placeholder="Amount" value={amount} onChange={(e) => /^\d*\.?\d*$/.test(e.target.value) && setAmount(e.target.value)} aria-label="Amount" />
              </span>
            )}
            <input className={cx(input, 'min-w-72 flex-1')} placeholder="Reason (shown on the client's statement)" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason" />
            <button type="button" className={primary} disabled={busy || !reason.trim() || (needsAmount && !amount)} onClick={() => void submit()}>
              {busy ? <Spinner /> : 'Submit'}
            </button>
          </div>
          {action === 'balance_transfer' && (
            <p className="m-0 text-[11px] text-muted">The client's cash from their previous broker, before you transfer their trades. Shown as "Balance transfer" on the statement; never counts as a deposit. Transferred trades then add or take their own result.</p>
          )}
          {action === 'zero_balance' && <p className="m-0 text-[11px] text-muted">Books the cash balance back to 0 (credit is not touched). Only with no open positions or pending orders.</p>}
          {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
        </div>
      )}
    </Panel>
  );
}

function CloseAll({ ctx }: { ctx: DeskCtx }) {
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const run = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<{ closed: number; failed: { error: string }[] }>(`/api/admin/desk/accounts/${ctx.accountId}/close-all`, { confirm });
      setMsg({ ok: !r.failed.length, text: `Closed ${r.closed}.${r.failed.length ? ` ${r.failed.length} failed: ${r.failed[0]!.error}` : ''}` });
      setConfirm('');
      ctx.refresh();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel label="Close all…" tone="border-down/40 text-down">
      {() => (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs">
            Type the account id <span className="num font-semibold">{ctx.accountId}</span> to close every open position at the live price.
          </span>
          <input className={cx(input, 'num w-96')} value={confirm} onChange={(e) => setConfirm(e.target.value.trim())} aria-label="Confirm account id" />
          <button type="button" className="h-9 rounded-lg bg-btn-short px-4 text-xs font-semibold text-white disabled:opacity-50" disabled={busy || confirm !== ctx.accountId} onClick={() => void run()}>
            {busy ? <Spinner /> : 'Close all'}
          </button>
          {msg && <span className={cx('text-xs', msg.ok ? 'text-up' : 'text-down')}>{msg.text}</span>}
        </div>
      )}
    </Panel>
  );
}

/** SL/TP and close buttons on one open position. */
/** Cancel one pending order. */
export function OrderActions({ id }: { id: string }) {
  const ctx = useDesk();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  if (!ctx?.canTrade) return null;
  const cancel = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.post(`/api/admin/desk/orders/${id}/cancel`, {});
      ctx.refresh();
    } catch (e) {
      setMsg(err(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="flex items-center gap-1">
      <button type="button" className={small} disabled={busy} onClick={() => void cancel()}>
        {busy ? <Spinner /> : 'Cancel'}
      </button>
      {msg && <span className="text-[11px] text-down">{msg}</span>}
    </span>
  );
}

// ---------- corrections (phase 4): the reason codes; the edit pop-up is in AdminDeskPositionDialogs.tsx ----------

export const REASONS: [string, string][] = [
  ['off_market_tick', 'Off-market tick'],
  ['duplicate_trade', 'Duplicate trade'],
  ['system_fault', 'System fault'],
  ['wrong_rate', 'Wrong rate applied'],
  ['holiday_charged', 'Holiday charged'],
  ['wrong_commission', 'Wrong commission'],
  ['dividend', 'Dividend'],
  ['contract_roll', 'Contract roll'],
  ['other', 'Other'],
];
